import React, { useMemo } from "react";
import { Dashboard } from "components/pages/dashboard/components/interfaces";
import { ReportOptions } from "models/component-print-models";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { isNumeric } from "utils/utils";
import IComponentTimed = Dashboard.IComponentTimed;
import DashboardComponentPropsBase = Dashboard.DashboardComponentPropsBase;
import SparkLineHandler from "./SparkLineHandler";


export interface SparkLineProps
  extends IComponentTimed,
    DashboardComponentPropsBase<ReportOptions> {
  valueScaling?: number;
  decimalPlaces?: number;
  labelFontSize?: string;
  sparkLineWidth: number;
  dataset: string;
  count?: number;
  unit?: string;
  showMinMax?: boolean;
  showCurrent?: boolean;
  updateFrequency?: number;
}

export default function SparkLine(props: SparkLineProps) {
  const count = props.count || 14;
  const dataset = isNumeric(props.dataset)
    ? `${props.dataset}:CATEGORIZED_DATASET`
    : (props.dataset as string);

  const { data, isLoading } = useGetRecentData([dataset], {
    count,
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
    },
  });
  const transformedData = useMemo(() => {
    if (!data || data.length === 0) return { dataArray: [], lastValueTime: null };

    const ob = data[0];
    let tempDataArr: number[] = [];

    if (props.showCurrent && ob.data.length > 1) {
      const currentTime = new Date().getTime();
      const diffBetweenTwoDatePoints =
        new Date(ob.data[1].key).getTime() - new Date(ob.data[0].key).getTime();
      const lastGoodValueTime = new Date(ob.data[ob.data.length - 1].key).getTime();
      const pointsBetween = Math.floor(
        (currentTime - lastGoodValueTime) / diffBetweenTwoDatePoints
      );
      const overlappingDates = pointsBetween <= count;
      const pointsDifference = count - pointsBetween;

      if (!overlappingDates) {
        tempDataArr = ob.data.map((item: InstantDoubleValue) => item.value);
      } else if (ob.data.length < count) {
        tempDataArr.length = ob.data.length;
        tempDataArr.fill(0);
        for (let i = 0; i < ob.data.length; i++) {
          tempDataArr[i] = ob.data[i].value;
        }
      } else {
        tempDataArr.length = count;
        tempDataArr.fill(0);
        for (let i = 0; i < pointsDifference; i++) {
          tempDataArr[i] = ob.data[i].value;
        }
      }
    } else {
      tempDataArr = ob.data.map((val: InstantDoubleValue) => val.value);
    }

    if (props.valueScaling !== 1) {
      tempDataArr = tempDataArr.map((val: number) => val * props.valueScaling!);
    }

    const lastValueTime = ob.data.length !== 0 ? new Date(ob.data[ob.data.length - 1].key as string) : null;

    return { dataArray: tempDataArr, lastValueTime };
  }, [data, props.showCurrent, props.valueScaling, count]);

  if (isLoading) {
    return <i className="pi pi-spin pi-spinner" style={{ fontSize: "4em" }} />;
  }

  return data && data.length !== 0 ? (
    <SparkLineHandler
      dataArray={transformedData.dataArray}
      lastValueTime={transformedData.lastValueTime}
      decimalPlaces={props.decimalPlaces}
      labelFontSize={props.labelFontSize}
      sparklineWidth={props.sparkLineWidth}
      unit={props.unit}
      showMinMax={props.showMinMax}
      dataset={dataset}
      title={data[0].title}
    />
  ) : (
    <div>
      <i className="pi pi-spin pi-spinner" style={{ fontSize: "4em" }} />
    </div>
  );
}
